import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { getProject, getProjects } from "../actions/projects/ProjectsActions";
import { ActionType } from "../constants/ActionTypes";
import ProjectStateEnum from "../constants/ProjectStateEnum";
import { RoutePath } from "../constants/SitePaths";
import { Auth } from "../services/Auth";
import { canonicalBackendId, getIframePartnerContext } from "../services/IframePartnerContext";
import Can from "../services/permisions/Can";

const requestOptions = { suppressProjectNotFoundRedirect: true };

function LaunchFailure() {
	return (
		<div role="alert">
			<FormattedMessage
				id="iframe_partner_launch_failed"
				defaultMessage="Unable to open this partner's Builder project. Check your access and reopen Builder from the partner."
			/>
		</div>
	);
}

function validProject(project) {
	return (
		project &&
		typeof project === "object" &&
		!Array.isArray(project) &&
		canonicalBackendId(project.id) !== null &&
		canonicalBackendId(project.partnerId) !== null &&
		canonicalBackendId(project.parrentPartnerId) !== null &&
		(project.state === ProjectStateEnum.Active || project.state === ProjectStateEnum.Trial)
	);
}

function ResolvePartner({ partnerId, windowObject }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		let cancelled = false;
		setFailed(false);
		async function resolve() {
			try {
				// Never use the cached list or selectedProject: both may belong to another launch.
				const result = await dispatch(getProjects(undefined, requestOptions));
				if (cancelled) return;
				if (result?.type !== ActionType.GET_PROJECTS_SUCCESS) throw new Error("Project list failed");
				// The picker uses the projects envelope; also accept the documented direct array response.
				const projects = Array.isArray(result.payload) ? result.payload : result.payload?.projects;
				if (!Array.isArray(projects)) throw new Error("Invalid project list");
				const matches = projects.filter(project => canonicalBackendId(project?.partnerId) === partnerId);
				if (matches.length !== 1 || !validProject(matches[0])) throw new Error("No unique allowed project");
				const candidate = matches[0];
				const project = await dispatch(getProject(canonicalBackendId(candidate.id), requestOptions));
				if (cancelled) return;
				if (
					!validProject(project) ||
					canonicalBackendId(project.id) !== canonicalBackendId(candidate.id) ||
					canonicalBackendId(project.partnerId) !== partnerId ||
					canonicalBackendId(project.parrentPartnerId) !== canonicalBackendId(candidate.parrentPartnerId)
				) {
					throw new Error("Invalid project detail");
				}
				// Parent pushState may change the brand while either request is pending. Fail closed.
				if (getIframePartnerContext(windowObject)?.partnerId !== partnerId) throw new Error("Partner changed");
				const query = new URLSearchParams(search);
				[...query.keys()].forEach(key => {
					if (/^(token|access_?token|refresh_?token|id_?token|authorization)$/i.test(key)) query.delete(key);
				});
				query.set("cid", String(canonicalBackendId(project.parrentPartnerId)));
				query.set("p", String(canonicalBackendId(project.id)));
				navigate(`${RoutePath.CUSTOMIZE}/${canonicalBackendId(project.id)}?${query}`, { replace: true });
			} catch {
				if (!cancelled) setFailed(true);
			}
		}
		resolve();
		return () => {
			cancelled = true;
		};
	}, [dispatch, navigate, partnerId, search, windowObject]);

	return failed ? (
		<LaunchFailure />
	) : (
		<div role="status">
			<FormattedMessage id="iframe_partner_launch_pending" defaultMessage="Opening the partner's Builder project…" />
		</div>
	);
}

export default function RootRedirect({ userInitialized, windowObject = window }) {
	const location = useLocation();
	const context = getIframePartnerContext(windowObject);
	const authenticated = Auth.isAuthenticated();
	if (!context || !authenticated) {
		return <Navigate to={authenticated ? RoutePath.PROJECTS : RoutePath.LOGIN} state={{ from: location }} replace />;
	}
	if (userInitialized !== true || context.partnerId === null) return <LaunchFailure />;
	return (
		<Can I="view" a="page.uiCustomization" fallback={<LaunchFailure />}>
			<ResolvePartner partnerId={context.partnerId} windowObject={windowObject} />
		</Can>
	);
}
